// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { TaskType } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useReactFlow } from '@xyflow/react';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useAssemblyEditor } from 'modules/Shop/CatalogSetup/PartEditor/useAssemblyEditor';
import { useCreateNodesFromTasks } from 'modules/Shop/CatalogSetup/PartEditor/utils';
import { TaskAdder } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/TaskAdder';
import { TaskTypesProvider } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/useTaskTypes';

const AddTaskToAssemblyWrapped = () => {
  const { createNodesFromTasks } = useCreateNodesFromTasks();
  const { addNode, publishing } = useAssemblyEditor();
  const { fitView, getZoom } = useReactFlow();

  const addSelectedTasks = (selectedTasks: TaskType[]) => {
    createNodesFromTasks(selectedTasks).map((v) => addNode(v));
    setTimeout(() => {
      void fitView({
        duration: 300,
        maxZoom: getZoom(),
        padding: 0,
      });
    }, 250);
  };

  return (
    <TaskAdder
      rightSideComponent={(selectedTasks, _, reset) => (
        <Button
          onClick={() => {
            addSelectedTasks(selectedTasks);
            reset();
            notifications.show({
              title: 'Successfully added',
              message: `Added ${selectedTasks.length} task${selectedTasks.length === 1 ? '' : 's'} to assembly.`,
              color: 'green',
            });
          }}
          disabled={selectedTasks.length === 0 || publishing}
        >
          Add to assembly
        </Button>
      )}
      hideGroupSelect
      hideDelete
    />
  );
};

export const AddTaskToAssembly = ({ disabled }: { disabled?: boolean }) => {
  const [opened, setOpened] = useState(false);
  const { publishing } = useAssemblyEditor();
  return (
    <>
      <Button onClick={() => setOpened(true)} variant="light" size="sm" disabled={publishing || disabled}>
        Add tasks
      </Button>
      <TaskTypesProvider>
        <WrappedModal
          title="Add tasks to assembly"
          opened={opened}
          hideModalButtons
          onClose={() => setOpened(false)}
          size="lg"
          tall
        >
          <AddTaskToAssemblyWrapped />
        </WrappedModal>
      </TaskTypesProvider>
    </>
  );
};
